import { InfoCircleOutlined, WarningTwoTone } from "@ant-design/icons";
import { SessionHistoryQueryRow } from "@p0security/shared/integrations/sessions/types";
import { PermissionRequest } from "@p0security/shared/types/permission";
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  Select,
  Spacing,
  Spin,
  Switch,
  Typography,
} from "@p0security/ui";
import { Export, ExportOption } from "components/Export";
import { GraphTooltip } from "components/GraphTooltip";
import { ChangeEvent, useCallback } from "react";

import { getUserIdentity } from "../sessionUtils";
import { QueryResultRowsTable } from "./QueryResultRowsTable";

const graphTooltipContent = (
  <div>
    <Typography.Text>
      This will incur additional costs upon usage. Please contact P0 support or
      refer to{" "}
      <a href="https://aws.amazon.com/cloudtrail/pricing/">
        AWS CloudTrail pricing
      </a>{" "}
      for more information.
    </Typography.Text>
  </div>
);

export const SessionTable = ({
  rows,
  error,
  loading,
  quantity,
  updateQuantity,
  search,
  updateSearch,
  includeResourceSearch,
  updateIncludeResourceSearch,
  getSession,
  exportOptions,
  requestId,
  requestType,
}: {
  rows: SessionHistoryQueryRow[] | undefined;
  error: string | undefined;
  loading: boolean;
  quantity: number;
  updateQuantity: (value: number) => void;
  search: string;
  updateSearch: (value: ChangeEvent<HTMLInputElement>) => void;
  includeResourceSearch: boolean;
  updateIncludeResourceSearch: (value: boolean) => void;
  getSession: () => void;
  exportOptions: Record<string, ExportOption<SessionHistoryQueryRow>>;
  requestId: string;
  requestType: PermissionRequest["type"];
}) => {
  const isAws = requestType === "aws";

  const handleSearch = useCallback(() => {
    getSession();
  }, [getSession]);

  if (!isAws) {
    // TODO: Enable for other request types
    return null;
  }

  return (
    <Flex vertical gap="middle">
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
        />
      )}
      {rows ? (
        <Spacing mt={16}>
          <Card
            title={
              <Flex align="center" justify="space-between">
                <span>Session History</span>
                <Export
                  data={rows}
                  filename={`session-history-${getUserIdentity(
                    rows?.[0]?.userIdentity
                  )}-${requestId}`}
                  title="Export session history"
                  options={exportOptions}
                />
              </Flex>
            }
          >
            <QueryResultRowsTable
              rows={rows}
              update={
                <Flex gap="small" align="center">
                  <Input
                    placeholder="Search relevant logs (e.g. s3, aws-cli, <resource-name>)"
                    value={search}
                    onChange={updateSearch}
                    onPressEnter={handleSearch}
                    allowClear
                    /* eslint-disable-line react/forbid-component-props */ style={{
                      width: 400,
                    }}
                    size="small"
                  />
                  {search && (
                    <Flex gap="small" align="center">
                      <Typography.Text>Include resources</Typography.Text>
                      <GraphTooltip title="This will incorporate an additional search for resources.">
                        <InfoCircleOutlined />
                      </GraphTooltip>
                      <Switch
                        checked={includeResourceSearch}
                        onChange={updateIncludeResourceSearch}
                      />
                    </Flex>
                  )}
                  <Typography.Text type="secondary">
                    Showing latest
                  </Typography.Text>
                  <Select
                    value={quantity}
                    onChange={updateQuantity}
                    /* eslint-disable-line react/forbid-component-props */ style={{
                      width: "64px",
                    }}
                    size="small"
                  >
                    <Select.Option value={10}>10</Select.Option>
                    <Select.Option value={20}>20</Select.Option>
                    <Select.Option value={50}>50</Select.Option>
                    <Select.Option value={100}>100</Select.Option>
                  </Select>
                  <Button
                    disabled={loading}
                    type="primary"
                    onClick={getSession}
                    size="small"
                    /* eslint-disable-line react/forbid-component-props */ style={{
                      width: "fit-content",
                    }}
                  >
                    {loading && <Spin size="small" />}
                    {loading ? "Fetching..." : search ? "Search" : "Update"}
                  </Button>
                </Flex>
              }
            />
          </Card>
        </Spacing>
      ) : (
        <Button
          disabled={loading}
          type="primary"
          onClick={getSession}
          /* eslint-disable-line react/forbid-component-props */ style={{
            width: "fit-content",
          }}
        >
          {loading && <Spin />}
          {loading ? "Preparing..." : "View Session History"}
          <GraphTooltip title={graphTooltipContent}>
            <Spacing asChild ml="8px">
              <WarningTwoTone />
            </Spacing>
          </GraphTooltip>
        </Button>
      )}
    </Flex>
  );
};
